import React, { useEffect, useState } from 'react';
import { getTenant, formatCurrencyAmount } from '../../../theme/tokens';
import { bankName } from '../../../shared/config/localeProfile';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ShoppingBagIcon, ChevronLeftIcon, PlusIcon, TagIcon, PackageIcon, SearchIcon, FilterIcon, LayoutGridIcon, ListIcon, QrCodeIcon, LinkIcon, TrashIcon, EditIcon, EyeIcon, DollarSignIcon, ArrowLeftIcon, StoreIcon, CheckCircleIcon, XCircleIcon, ImageIcon } from 'lucide-react';
import { Shop } from './Shops';
import { ShopDashboard } from '../components/ShopDashboard';
// Product type definition
interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  discountedPrice?: number;
  categoryId: string;
  imageUrl: string;
  stockQuantity: number;
  sku: string;
  isActive: boolean;
}
// Category type definition
interface Category {
  id: string;
  name: string;
  description: string;
  productCount: number;
}
export function ShopDetails() {
  const {
    t
  } = useTranslation('shops');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const shopId = searchParams.get('id');
  const isNewShop = searchParams.get('new') === 'true';
  const isEditMode = searchParams.get('edit') === 'true' || isNewShop;
  // Shop state
  const [shop, setShop] = useState<Shop | null>(null);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'products' | 'categories' | 'settings'>('dashboard');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | 'all'>('all');
  // Products and categories state
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  // Form state
  const [shopForm, setShopForm] = useState({
    name: '',
    description: '',
    logo: '•',
    color: '#f8d7d7',
    channels: ['ecommerce'] as ('pos' | 'ecommerce')[]
  });
  // Product form state
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: 0,
    discountedPrice: undefined as number | undefined,
    categoryId: '',
    imageUrl: '',
    stockQuantity: 0,
    sku: '',
    isActive: true
  });
  // Mock data loading
  useEffect(() => {
    if (shopId && !isNewShop) {
      // Mock shop data
      const mockShop: Shop = {
        id: shopId,
        name: 'Beach Shop',
        description: 'Beachwear and accessories for summer holidays',
        logo: '•',
        color: '#f8d7d7',
        isOpen: true,
        channels: ['pos', 'ecommerce'],
        productsCount: 48,
        revenue: {
          total: 12580,
          currency: getTenant().currency.code,
          growth: 15.3
        },
        ordersCount: 124
      };
      setShop(mockShop);
      setShopForm({
        name: mockShop.name,
        description: mockShop.description,
        logo: mockShop.logo,
        color: mockShop.color,
        channels: [...mockShop.channels]
      });
      // Mock categories
      setCategories([{
        id: 'cat1',
        name: 'Swimwear',
        description: 'Swimsuits and beachwear',
        productCount: 12
      }, {
        id: 'cat2',
        name: 'Accessories',
        description: 'Beach accessories',
        productCount: 18
      }, {
        id: 'cat3',
        name: 'Footwear',
        description: 'Sandals and beach shoes',
        productCount: 8
      }, {
        id: 'cat4',
        name: 'Souvenirs',
        description: 'Memorable items from the beach',
        productCount: 10
      }]);
      // Mock products
      setProducts([{
        id: 'prod1',
        name: 'Beach Towel',
        description: 'Large absorbent beach towel',
        price: 29.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1601924582970-9238bcb495d9?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 45,
        sku: 'BT-001',
        isActive: true
      }, {
        id: 'prod2',
        name: 'Sunglasses',
        description: 'UV protection sunglasses',
        price: 49.99,
        discountedPrice: 39.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1577803645773-f96470509666?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 32,
        sku: 'SG-002',
        isActive: true
      }, {
        id: 'prod3',
        name: 'Swim Shorts',
        description: 'Quick-dry swim shorts for men',
        price: 34.99,
        categoryId: 'cat1',
        imageUrl: 'https://images.unsplash.com/photo-1565992441121-4367c2967103?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
        stockQuantity: 28,
        sku: 'SS-003',
        isActive: true
      }, {
        id: 'prod4',
        name: 'Beach Sandals',
        description: 'Comfortable sandals for the beach',
        price: 24.99,
        categoryId: 'cat3',
        imageUrl: 'https://images.unsplash.com/photo-1603487742131-4160ec999306?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 50,
        sku: 'BS-004',
        isActive: true
      }, {
        id: 'prod5',
        name: 'Straw Hat',
        description: 'Wide brim straw hat for sun protection',
        price: 19.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1572307480813-ceb0e59d8325?w=300&q=80',
        stockQuantity: 15,
        sku: 'SH-005',
        isActive: true
      }, {
        id: 'prod6',
        name: 'Beach Bag',
        description: 'Spacious waterproof beach bag',
        price: 39.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1590874103328-eac38a683ce7?w=300&q=80',
        stockQuantity: 20,
        sku: 'BB-006',
        isActive: true
      }, {
        id: 'prod7',
        name: 'Surfboard Wax',
        description: 'High quality organic surfboard wax',
        price: 9.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1531722569936-825d3dd91b15?w=300&q=80',
        stockQuantity: 100,
        sku: 'SW-007',
        isActive: true
      }, {
        id: 'prod8',
        name: 'Snorkeling Set',
        description: 'Professional snorkeling mask and fins',
        price: 59.99,
        discountedPrice: 49.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=300&q=80',
        stockQuantity: 12,
        sku: 'SN-008',
        isActive: true
      }]);
    }
  }, [shopId, isNewShop]);
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) || product.description.toLowerCase().includes(searchTerm.toLowerCase()) || product.sku.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategoryId === 'all' || product.categoryId === selectedCategoryId;
    return matchesSearch && matchesCategory;
  });
  const saveShop = (e: React.FormEvent) => {
    e.preventDefault();
    if (isNewShop) {
      navigate('/shops');
    } else {
      navigate(`/shop-details?id=${shopId}`);
    }
  };
  const toggleProductStatus = (productId: string) => {
    setProducts(products.map(product => product.id === productId ? {
      ...product,
      isActive: !product.isActive
    } : product));
  };
  const handleAddProductClick = () => {
    setEditingProduct(null);
    setProductForm({
      name: '',
      description: '',
      price: 0,
      discountedPrice: undefined,
      categoryId: categories.length > 0 ? categories[0].id : '',
      imageUrl: '',
      stockQuantity: 0,
      sku: '',
      isActive: true
    });
    setShowProductForm(true);
  };
  const handleEditProductClick = (product: Product) => {
    // Navigate to dedicated edit page
    navigate(`/product-edit/${product.id}?shop=${shopId}`);
  };
  const handleDeleteProductClick = (productId: string) => {
    if (confirm('Are you sure you want to delete this product?')) {
      setProducts(products.filter(p => p.id !== productId));
    }
  };
  const handleProductFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingProduct) {
      setProducts(products.map(p => p.id === editingProduct.id ? {
        ...productForm,
        id: p.id
      } as Product : p));
    } else {
      const newProduct: Product = {
        ...productForm,
        id: `prod${Date.now()}`
      } as Product;
      setProducts([...products, newProduct]);
    }
    setShowProductForm(false);
  };
  // Render Form Mode
  if (isEditMode && !showProductForm) {
    return <div className="space-y-8 max-w-7xl mx-auto pb-12" data-id="element-12778" data-mp="274j4smk4vd5g">
        <div className="flex items-center" data-id="element-12779" data-mp="271wn3u3j3h9i">
          <button onClick={() => navigate('/shops')} className="flex items-center text-gray-900 hover:text-gray-600 transition-colors text-lg font-semibold" data-id="element-12780" data-mp="1t59m9g8gg6yb">
            <ArrowLeftIcon size={20} className="mr-2" data-id="element-12781" data-mp="3ni9a1wmzo7go" />
            {isNewShop ? 'All shops' : 'Back to shop'}
          </button>
        </div>

        <form onSubmit={saveShop} className="space-y-6" data-id="element-12782" data-mp="3sguanx65kt5n">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-12783" data-mp="36j1fw9b9s477">
            <div className="p-8 border-b border-gray-100" data-id="element-12784" data-mp="3ba9qv9s8zxmh">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6" data-id="element-12785" data-mp="3bbupbahoo0pm">
                {isNewShop ? 'New Shop' : 'Edit Shop'}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-12786" data-mp="31kq5nxh2brog">
                <div data-id="element-12787" data-mp="3p9j4q2szefna">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-12788" data-mp="3paatous2pf4l">
                    {t('pages.ShopDetails.shop_name')}{' '}
                    <span className="text-red-500" data-id="element-12789" data-mp="3g933z13mhmm4">*</span>
                  </label>
                  <input type="text" required value={shopForm.name} onChange={e => setShopForm({
                  ...shopForm,
                  name: e.target.value
                })} placeholder={t('pages.ShopDetails.attrs.enter_shop_name')} className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-12790" data-mp="3gbfl5c1k3sy4" />
                </div>

                <div data-id="element-12791" data-mp="26atpjmadn9ef">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-12792" data-mp="26bmmxwng4mjw">
                    {t('pages.ShopDetails.shop_logo')}
                  </label>
                  <input type="text" value={shopForm.logo} onChange={e => setShopForm({
                  ...shopForm,
                  logo: e.target.value
                })} placeholder={t('pages.ShopDetails.attrs.logo_character_or_emoji')} maxLength={2} className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-12793" data-mp="1dmxtmjsmtlip" />
                  <p className="mt-1.5 text-xs text-gray-500" data-id="element-12794" data-mp="34nz63rkvkr8m">
                    {t('pages.ShopDetails.enter_a_single_character_or_emoji')}
                  </p>
                </div>

                <div className="md:col-span-2" data-id="element-12795" data-mp="38rf2qnwy2rlb">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-12796" data-mp="2c0d5ms6y22pz">
                    {t('pages.CampaignDetails.description')}
                  </label>
                  <textarea value={shopForm.description} onChange={e => setShopForm({
                  ...shopForm,
                  description: e.target.value
                })} rows={3} placeholder={t('pages.ShopDetails.attrs.describe_your_shop')} className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-12797" data-mp="2l09o2c2qq5cl" />
                </div>
              </div>
            </div>

            <div className="p-8" data-id="element-12798" data-mp="1lxgaxftgbze">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6" data-id="element-12799" data-mp="1kw2wmbbvdei">
                {t('pages.ShopDetails.appearance_channels')}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-12800" data-mp="3nsce4wjydpc8">
                <div data-id="element-12801" data-mp="3itiyaxh71m8w">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-12802" data-mp="3isr67wmt26nd">
                    {t('pages.ShopDetails.logo_background_color')}
                  </label>
                  <div className="flex items-center gap-3" data-id="element-12803" data-mp="1h1w6wute3pbw">
                    <input type="color" value={shopForm.color} onChange={e => setShopForm({
                    ...shopForm,
                    color: e.target.value
                  })} className="w-10 h-10 border-0 p-0 rounded cursor-pointer" data-id="element-12804" data-mp="1h1czi81d8jc4" />
                    <input type="text" value={shopForm.color} onChange={e => setShopForm({
                    ...shopForm,
                    color: e.target.value
                  })} className="flex-1 px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors uppercase" data-id="element-12805" data-mp="1b5fclm00p4od" />
                  </div>
                </div>

                <div data-id="element-12806" data-mp="igrir217d6z7">
                  <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-12807" data-mp="nd8h0p2491q0">
                    {t('pages.ShopDetails.shop_channels')}
                  </label>
                  <div className="space-y-3" data-id="element-12808" data-mp="3tsu1etqcqo6">
                    <label className="flex items-center cursor-pointer group" data-id="element-12809" data-mp="3uyswrqo2dbvs">
                      <input type="checkbox" checked={shopForm.channels.includes('ecommerce')} onChange={e => {
                      const newChannels = e.target.checked ? [...shopForm.channels, 'ecommerce'] : shopForm.channels.filter(c => c !== 'ecommerce');
                      setShopForm({
                        ...shopForm,
                        channels: newChannels as ('pos' | 'ecommerce')[]
                      });
                    }} className="h-4 w-4 text-[#E2231A] focus:ring-[#E2231A] border-gray-300 rounded transition-colors" data-id="element-12810" data-mp="cs4u07ghl0ta" />
                      <span className="ml-3 text-sm text-gray-700 group-hover:text-gray-900 transition-colors" data-id="element-12811" data-mp="10jsdi4bf59lj">
                        {t('pages.ShopDetails.ecommerce_online_shop')}
                      </span>
                    </label>
                    <label className="flex items-center cursor-pointer group" data-id="element-12812" data-mp="75hkqnaglmon">
                      <input type="checkbox" checked={shopForm.channels.includes('pos')} onChange={e => {
                      const newChannels = e.target.checked ? [...shopForm.channels, 'pos'] : shopForm.channels.filter(c => c !== 'pos');
                      setShopForm({
                        ...shopForm,
                        channels: newChannels as ('pos' | 'ecommerce')[]
                      });
                    }} className="h-4 w-4 text-[#E2231A] focus:ring-[#E2231A] border-gray-300 rounded transition-colors" data-id="element-12813" data-mp="73ek79bokb5o" />
                      <span className="ml-3 text-sm text-gray-700 group-hover:text-gray-900 transition-colors" data-id="element-12814" data-mp="2p2xl66s7ths5">
                        {t('pages.ShopDetails.point_of_sale_physical_store')}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3" data-id="element-12815" data-mp="2bd3urk12apgb">
            <button type="button" onClick={() => navigate(isNewShop ? '/shops' : `/shop-details?id=${shopId}`)} className="px-6 py-2.5 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-12816" data-mp="380gwxi625ei9">
              {t('pages.CampaignDetails.cancel')}
            </button>
            <button type="submit" className="px-6 py-2.5 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-12817" data-mp="1nmg9lx24y3bt">
              {isNewShop ? 'Create shop' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>;
  }
  // Render Product Form Mode
  if (showProductForm) {
    return <div className="space-y-8 max-w-3xl mx-auto pb-12" data-id="element-12818" data-mp="38ryrzxln1yfp">
        <div className="flex items-center" data-id="element-12819" data-mp="3dpibbbx711ei">
          <button onClick={() => setShowProductForm(false)} className="flex items-center text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-12820" data-mp="3dqa3ecrl0h01">
            <ArrowLeftIcon size={16} className="mr-2" data-id="element-12821" data-mp="a8i7woe8lex3" />
            {t('pages.ShopDetails.back_to_products')}
          </button>
        </div>

        <form onSubmit={handleProductFormSubmit} className="space-y-6" data-id="element-12822" data-mp="j8d30s0di5a">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-12823" data-mp="h5dpq4yucnw">
            <div className="px-8 py-6 border-b border-gray-100" data-id="element-12824" data-mp="o6py8dgrphza">
              <h1 className="text-xl font-semibold text-gray-900" data-id="element-12825" data-mp="t0kojzqvov7c">
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </h1>
            </div>

            <div className="p-8 space-y-10" data-id="element-12826" data-mp="28rd7j6i6cop4">
              {/* Product Information */}
              <div className="space-y-6" data-id="element-12827" data-mp="2mi07t33l2mnb">
                <h2 className="text-lg font-medium text-gray-900" data-id="element-12828" data-mp="2mgyl21d7gbpr">
                  {t('pages.ShopDetails.product_information')}
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-12829" data-mp="1ur2d5thnorz7">
                  <div data-id="element-12830" data-mp="1ut6imfv7dz1l">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-12831" data-mp="1przcsmcmf9xk">
                      {t('pages.ShopDetails.product_name')}{' '}
                      <span className="text-red-500" data-id="element-12832" data-mp="23tbqss8811gt">*</span>
                    </label>
                    <input type="text" required value={productForm.name} onChange={e => setProductForm({
                    ...productForm,
                    name: e.target.value
                  })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-12833" data-mp="1yrw3sumhgkvs" />
                  </div>

                  <div data-id="element-12834" data-mp="34xa2ech6evu2">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-12835" data-mp="34wqlmz39idhm">
                      {t('pages.ShopDetails.sku')}
                    </label>
                    <input type="text" value={productForm.sku} onChange={e => setProductForm({
                    ...productForm,
                    sku: e.target.value
                  })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-12836" data-mp="3dyrhbriukrh6" />
                  </div>

                  <div className="md:col-span-2" data-id="element-12837" data-mp="k5wnkucfhm8i">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-12838" data-mp="p4q3etf6tpbu">
                      {t('pages.CampaignDetails.description')}
                    </label>
                    <textarea value={productForm.description} onChange={e => setProductForm({
                    ...productForm,
                    description: e.target.value
                  })} rows={3} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-12839" data-mp="g3i5wdkhwsit" />
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="relative py-2" data-id="element-12840" data-mp="3jki8wzzjiczp">
                <div className="absolute inset-0 flex items-center" data-id="element-12841" data-mp="3jmbeebyf2r64">
                  <div className="w-full border-t border-gray-200" data-id="element-12842" data-mp="1p4tj1zhxbciw"></div>
                </div>
                <div className="relative flex justify-center" data-id="element-12843" data-mp="1kc8qt2eo17hh">
                  <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-12844" data-mp="1gbghya9ph4lq">
                    {t('pages.ShopDetails.pricing_inventory')}
                  </span>
                </div>
              </div>

              {/* Pricing & Inventory */}
              <div className="space-y-5" data-id="element-12845" data-mp="280jndtdbwkzy">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-12846" data-mp="242c70zms1lpa">
                  <div data-id="element-12847" data-mp="1z1xylgh9k9qq">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-12848" data-mp="1z14yuzgq2ki3">
                      {t('pages.ShopDetails.price')}{' '}
                      <span className="text-red-500" data-id="element-12849" data-mp="1u57hqqxmxa49">*</span>
                    </label>
                    <div className="relative" data-id="element-12850" data-mp="nocalyn54e6s">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-12851" data-mp="ines1ky7xi4r">
                        <span className="text-gray-500 sm:text-sm" data-id="element-12852" data-mp="8ksqvo5f2wwl">
                          {t('components.ShopDashboard.usd_2')}
                        </span>
                      </div>
                      <input type="number" required min="0" step="0.01" value={productForm.price || ''} onChange={e => setProductForm({
                      ...productForm,
                      price: parseFloat(e.target.value) || 0
                    })} className="w-full pl-12 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-12853" data-mp="ihnlhvykkd5l" />
                    </div>
                  </div>

                  <div data-id="element-12854" data-mp="37zxrt3uphzyn">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-12855" data-mp="3818uf0cf11o6">
                      {t('pages.ShopDetails.discounted_price')}
                    </label>
                    <div className="relative" data-id="element-12856" data-mp="xb9cn65cz00f">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-12857" data-mp="1bbaeantg6bgy">
                        <span className="text-gray-500 sm:text-sm" data-id="element-12858" data-mp="16bxt07i85cgh">
                          {t('components.ShopDashboard.usd_2')}
                        </span>
                      </div>
                      <input type="number" min="0" step="0.01" value={productForm.discountedPrice || ''} onChange={e => setProductForm({
                      ...productForm,
                      discountedPrice: e.target.value ? parseFloat(e.target.value) : undefined
                    })} className="w-full pl-12 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-12859" data-mp="eljlct0t0wd9" />
                    </div>
                  </div>

                  <div data-id="element-12860" data-mp="22isplitatuip">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-12861" data-mp="22k3trjqnp4ac">
                      {t('pages.ShopDetails.stock_quantity')}
                    </label>
                    <input type="number" min="0" value={productForm.stockQuantity || ''} onChange={e => setProductForm({
                    ...productForm,
                    stockQuantity: parseInt(e.target.value) || 0
                  })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-12862" data-mp="2u9ymlqdq3c3r" />
                  </div>

                  <div data-id="element-12863" data-mp="lqf7vt2ycrwu">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-12864" data-mp="lr70qw560bjf">
                      {t('pages.CampaignDetails.category')}
                    </label>
                    <select value={productForm.categoryId} onChange={e => setProductForm({
                    ...productForm,
                    categoryId: e.target.value
                  })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors bg-white" data-id="element-12865" data-mp="bzsw69ycwljf">
                      {categories.map(c => <option key={c.id} value={c.id} data-id="element-12866" data-mp="1ng10ogxcm6xq">
                          {c.name}
                        </option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="relative py-2" data-id="element-12867" data-mp="1zwsdjl13rsne">
                <div className="absolute inset-0 flex items-center" data-id="element-12868" data-mp="24otkqsjeyt6k">
                  <div className="w-full border-t border-gray-200" data-id="element-12869" data-mp="24rpiogv9hhv0"></div>
                </div>
                <div className="relative flex justify-center" data-id="element-12870" data-mp="29q950idrovnw">
                  <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-12871" data-mp="1c1zbkrs1rg8e">
                    {t('pages.ShopDetails.product_image')}
                  </span>
                </div>
              </div>

              {/* Product Image */}
              <div className="space-y-5" data-id="element-12872" data-mp="1q0yrnb1humsy">
                <div data-id="element-12873" data-mp="1q1s0qinxdoe9">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-12874" data-mp="1py477jihd1zj">
                    {t('pages.ShopDetails.image_url')}
                  </label>
                  <input type="text" value={productForm.imageUrl} onChange={e => setProductForm({
                  ...productForm,
                  imageUrl: e.target.value
                })} placeholder="https://images.unsplash.com/..." className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-12875" data-mp="31e15hyc7ky9f" />
                </div>

                <div className="mt-4" data-id="element-12876" data-mp="52n4a9a3baol">
                  {productForm.imageUrl ? <div className="w-48 h-48 rounded-lg border border-gray-200 overflow-hidden bg-gray-50" data-id="element-12877" data-mp="jze2wgbxwid5">
                      <img src={productForm.imageUrl} alt={t('pages.ShopDetails.attrs.preview')} className="w-full h-full object-cover" data-id="element-12878" data-mp="f2nnlv7ylqss" />
                    </div> : <div className="w-48 h-48 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center bg-gray-50 text-gray-400" data-id="element-12879" data-mp="wbq0012jkwah">
                      <ImageIcon size={32} className="mb-2" data-id="element-12880" data-mp="mkvfp28uw6ro" />
                      <span className="text-sm" data-id="element-12881" data-mp="mklkn08yf9f4">
                        {t('pages.ShopDetails.no_image')}
                      </span>
                    </div>}
                </div>
              </div>

              {/* Divider */}
              <div className="relative py-2" data-id="element-12882" data-mp="hmabkrqhh2xy">
                <div className="absolute inset-0 flex items-center" data-id="element-12883" data-mp="hn3m83skccld">
                  <div className="w-full border-t border-gray-200" data-id="element-12884" data-mp="3n188sndm3be"></div>
                </div>
                <div className="relative flex justify-center" data-id="element-12885" data-mp="8kavq2r7g4wl">
                  <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-12886" data-mp="1y00siz2pblp0">
                    {t('components.CampaignList.status')}
                  </span>
                </div>
              </div>

              {/* Status */}
              <div className="space-y-5" data-id="element-12887" data-mp="34schqc5v5po4">
                <div data-id="element-12888" data-mp="39pl12g2qzzqy">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-12889" data-mp="39p2xeydraj90">
                    {t('components.CampaignList.status')}
                  </label>
                  <select value={productForm.isActive ? 'active' : 'inactive'} onChange={e => setProductForm({
                  ...productForm,
                  isActive: e.target.value === 'active'
                })} className="w-full md:w-1/2 px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors bg-white" data-id="element-12890" data-mp="3jfnswcy4v7np">
                    <option value="active" data-id="element-12891" data-mp="232fqwjgcxrtw">
                      {t('pages.Campaigns.active')}
                    </option>
                    <option value="inactive" data-id="element-12892" data-mp="2810pyfjvi5gr">
                      {t('pages.Campaigns.inactive')}
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-8 py-4 border-t border-gray-100 bg-gray-50" data-id="element-12893" data-mp="1vo6h0pu4l4qe">
              <button type="button" onClick={() => setShowProductForm(false)} className="px-6 py-2.5 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-12894" data-mp="1qotts3zcdvna">
                {t('pages.CampaignDetails.cancel')}
              </button>
              <button type="submit" className="px-6 py-2.5 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-12895" data-mp="29gd0akskrru2">
                {editingProduct ? 'Save changes' : 'Create product'}
              </button>
            </div>
          </div>
        </form>
      </div>;
  }
  // Render Detail Mode
  if (!shop) return null;
  return <div className="max-w-[1400px] mx-auto space-y-8 pb-12" data-id="element-12896" data-mp="3h78qzx3z78cg">
      <div className="flex items-center" data-id="element-12897" data-mp="3l9x085wlqh9y">
        <button onClick={() => navigate('/shops')} className="flex items-center text-gray-900 hover:text-gray-600 transition-colors text-lg font-semibold" data-id="element-12898" data-mp="3l8m1iki0jzpp">
          <ArrowLeftIcon size={20} className="mr-2" data-id="element-12899" data-mp="3ccd0x2iauv1z" />
          {t('pages.ShopDetails.all_shops')}
        </button>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-12900" data-mp="32cm1qnisnrvk">
        <div className="flex items-center gap-4" data-id="element-12901" data-mp="2okxqsnka79g6">
          <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm" style={{
          backgroundColor: shop.color
        }} data-id="element-12902" data-mp="2omioun5t1ais">
            <span className="text-2xl font-bold text-red-600" data-id="element-12903" data-mp="2eol2k57i9nst">{shop.logo}</span>
          </div>
          <div data-id="element-12904" data-mp="2jkt9wwfgocpf">
            <div className="flex items-center gap-3" data-id="element-12905" data-mp="2jnfhgw2cqs7n">
              <h1 className="text-2xl font-semibold text-gray-900" data-id="element-12906" data-mp="200kp5aq3gmhl">
                {shop.name}
              </h1>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${shop.isOpen ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-12907" data-mp="1vzsfwhh82hlb">
                {shop.isOpen ? 'Open' : 'Closed'}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-1.5" data-id="element-12908" data-mp="2ix43cefr90l0">
              {shop.channels.includes('pos') && <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100" data-id="element-12909" data-mp="2euzcfngoyi22">
                  {t('pages.ShopDetails.pos')}
                </span>}
              {shop.channels.includes('ecommerce') && <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100" data-id="element-12910" data-mp="32kjw68ko5jce">
                  {t('pages.ShopDetails.ecommerce')}
                </span>}
              <span className="text-sm text-gray-500 ml-1" data-id="element-12911" data-mp="zzh6vikbkqgz">
                {shop.description}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2" data-id="element-12912" data-mp="14ssgfrginlcl">
          <button onClick={() => navigate(`/shop-details?id=${shop.id}&edit=true`)} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-transparent hover:border-gray-200" title={t('pages.ShopDetails.attrs.edit_shop')} data-id="element-12913" data-mp="19or0x3yq4j7q">
            <EditIcon size={18} data-id="element-12914" data-mp="lyn0t6kqv1lh" />
          </button>
          <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-transparent hover:border-gray-200" title={t('pages.Sales.qr_code')} data-id="element-12915" data-mp="qzu68yzezooz">
            <QrCodeIcon size={18} data-id="element-12916" data-mp="22d4yxjptbfja" />
          </button>
          <button onClick={() => window.open(`https://shop.${bankName().toLowerCase().replace(/\s+/g, '')}.com.my/`, '_blank', 'noopener,noreferrer')} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-transparent hover:border-gray-200" title={t('pages.ShopDetails.attrs.shop_link')} data-id="element-12917" data-mp="3mwihk8mp4gci">
            <LinkIcon size={18} data-id="element-12918" data-mp="3366j03zp04m4" />
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-12919" data-mp="2z9afdzkkdnep">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-12920" data-mp="2u8czjtn115gd">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-12921" data-mp="2pab56ck5hjpl">
            <PackageIcon size={16} className="text-gray-500" data-id="element-12922" data-mp="sf5ahshm39ha" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-12923" data-mp="nhn0dyruyo7p">
              {t('pages.ShopDetails.products')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-12924" data-mp="2hw0vtq1x5ta2">
            <div className="text-2xl font-bold text-gray-900" data-id="element-12925" data-mp="2mo22mwgbirsp">
              {shop.productsCount}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-12926" data-mp="280gycz7pimj7">
            {t('pages.ShopDetails.active_items_in_catalog')}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-12927" data-mp="30njvbmwqip7c">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-12928" data-mp="35npr9fa1exv8">
            <DollarSignIcon size={16} className="text-gray-500" data-id="element-12929" data-mp="2qq5qkkk6bouv" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-12930" data-mp="2qpnimqo5fm74">
              {t('components.ShopDashboard.revenue')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-12931" data-mp="1fah21or9uhry">
            <div className="text-2xl font-bold text-gray-900" data-id="element-12932" data-mp="1fcuwbve4npyg">
              {t('components.ShopDashboard.usd_2')}{' '}
              {shop.revenue.total.toLocaleString()}
            </div>
          </div>
          <div className={`px-4 py-2 text-xs font-medium ${shop.revenue.growth >= 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-id="element-12933" data-mp="2eb40ywjwm1r">
            {shop.revenue.growth >= 0 ? '+' : ''}
            {shop.revenue.growth}
            {t('pages.ShopDetails.from_last_month')}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-12934" data-mp="3fqlckj17cw16">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-12935" data-mp="3asjqrqce6im0">
            <ShoppingBagIcon size={16} className="text-gray-500" data-id="element-12936" data-mp="3oj6igyjqj88l" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-12937" data-mp="3olj8lyzncowq">
              {t('pages.Orders.orders')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-12938" data-mp="2w2m2laegpswt">
            <div className="text-2xl font-bold text-gray-900" data-id="element-12939" data-mp="2w1ay18d70h4n">
              {shop.ordersCount}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-12940" data-mp="3gwb8218yz1kf">
            {t('pages.ShopDetails.total_completed_orders')}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-12941" data-mp="3lvo1j4ucj6vz">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-12942" data-mp="3pz3yy3e8pb9q">
            <StoreIcon size={16} className="text-gray-500" data-id="element-12943" data-mp="3uxdvahbpahwx" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-12944" data-mp="3qkvnhwk2qui">
              {t('components.CampaignList.status')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1 flex items-center" data-id="element-12945" data-mp="8mrpwdfladwm">
            {shop.isOpen ? <div className="flex items-center text-green-600" data-id="element-12946" data-mp="dhp9geahw5nr">
                <CheckCircleIcon size={24} className="mr-2" data-id="element-12947" data-mp="hfweibvljij4" />
                <span className="text-xl font-bold" data-id="element-12948" data-mp="mgj0zq8yy5ug">
                  {t('pages.ShopDetails.accepting_orders')}
                </span>
              </div> : <div className="flex items-center text-red-600" data-id="element-12949" data-mp="rd9j0j4c3bii">
                <XCircleIcon size={24} className="mr-2" data-id="element-12950" data-mp="11k3pn95gnio2" />
                <span className="text-xl font-bold" data-id="element-12951" data-mp="wkhb8wdwbwdo">
                  {t('pages.ShopDetails.currently_closed')}
                </span>
              </div>}
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-12952" data-mp="rmxxdxl41lm9">
            {t('pages.ShopDetails.toggle_in_edit_mode')}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-12953" data-mp="1kbmpx883oime">
        <div className="flex space-x-8" role="tablist" aria-label="Shop sections" data-id="element-12954" data-mp="1fbqn3cb52x8f">
          {[{
          id: 'dashboard',
          label: 'Dashboard'
        }, {
          id: 'products',
          label: 'Products'
        }, {
          id: 'categories',
          label: 'Categories'
        }, {
          id: 'messages',
          label: 'Messages'
        }, {
          id: 'settings',
          label: 'Settings'
        }].map(tab => <button key={tab.id} role="tab" aria-selected={activeTab === tab.id} aria-controls={`${tab.id}-tabpanel`} id={`shop-tab-${tab.id}`} onClick={() => tab.id === 'settings' ? navigate(`/shop-settings/${shopId || 'new'}`) : tab.id === 'messages' ? navigate(`/shop-settings/${shopId || 'new'}?section=messages`) : setActiveTab(tab.id as 'dashboard' | 'products' | 'categories' | 'settings')} className={`pb-4 -mb-px text-sm font-medium transition-colors border-b-2 ${activeTab === tab.id ? 'border-[#E2231A] text-[#9F140F]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-12955" data-mp="3op9vv8nq2dte">
              {tab.label}
            </button>)}
        </div>
      </div>

      {/* Tab Content */}
      <div className="pt-2" data-id="element-12956" data-mp="2quz7ub0vbwbo">
        {activeTab === 'dashboard' && <ShopDashboard shop={shop} data-id="element-12957" data-mp="2quh5cwnm4lvb" />}

        {activeTab === 'products' && <div className="space-y-6" data-id="element-12958" data-mp="1u5rkqyklpf5n">
            {/* Products Toolbar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-12959" data-mp="1z3ay7w9h5nwj">
              <div className="flex items-center gap-3" data-id="element-12960" data-mp="235o72tjiq1xj">
                <div className="relative w-full sm:w-64" data-id="element-12961" data-mp="23439sw5tk4vz">
                  <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-12962" data-mp="284r882wdno08" />

                  <input type="search" placeholder={t('pages.ShopDetails.attrs.search_products')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-12963" data-mp="3jjebk1ejus7p" />
                </div>

                <div className="relative" data-id="element-12964" data-mp="1oeoqq8puomvh">
                  <FilterIcon size={14} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-12965" data-mp="10ryob69laqac" />

                  <select value={selectedCategoryId} onChange={e => setSelectedCategoryId(e.target.value)} className="pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors appearance-none cursor-pointer" data-id="element-12966" data-mp="1ah8gsn8ciayz">
                    <option value="all" data-id="element-12967" data-mp="2ooy2y9kybjam">
                      {t('pages.ShopDetails.all_categories')}
                    </option>
                    {categories.map(c => <option key={c.id} value={c.id} data-id="element-12968" data-mp="2th96dmewhn7x">
                        {c.name}
                      </option>)}
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-3" data-id="element-12969" data-mp="37f608b1yzfiw">
                <div className="flex items-center bg-gray-100 rounded-lg p-1" data-id="element-12970" data-mp="37fp7mxtzulio">
                  <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`} title={t('pages.ShopDetails.attrs.grid_view')} data-id="element-12971" data-mp="3cd7m13qs5sy2">
                    <LayoutGridIcon size={16} data-id="element-12972" data-mp="1n1f6som7qvnf" />
                  </button>
                  <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`} title={t('pages.ShopDetails.attrs.list_view')} data-id="element-12973" data-mp="1rz711pabytus">
                    <ListIcon size={16} data-id="element-12974" data-mp="1xfwh5ciu9zlc" />
                  </button>
                </div>

                <button onClick={handleAddProductClick} className="flex items-center gap-2 px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-12975" data-mp="1tekt3bfm5mf9">
                  <PlusIcon size={16} data-id="element-12976" data-mp="2h570r1lph4oq" />
                  {t('pages.ShopDetails.add_product')}
                </button>
              </div>
            </div>

            {/* Products Grid/List */}
            {filteredProducts.length === 0 ? <div className="text-center py-16 bg-white rounded-xl border border-gray-200" data-id="element-12977" data-mp="15oqfgrngztty">
                <PackageIcon size={48} className="mx-auto text-gray-300 mb-4" data-id="element-12978" data-mp="10u313lm6kft3" />

                <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-12979" data-mp="3cgwoauatwb7t">
                  {t('pages.ShopDetails.no_products_found')}
                </h3>
                <p className="text-gray-500 mb-6" data-id="element-12980" data-mp="3lfi44hox1c4w">
                  {t('pages.ShopDetails.try_adjusting_your_search_or')}
                </p>
                <button onClick={handleAddProductClick} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors" data-id="element-12981" data-mp="3q8bdjivbx8nb">
                  <PlusIcon size={16} data-id="element-12982" data-mp="8zodgueiqbml" />
                  {t('pages.ShopDetails.add_new_product')}
                </button>
              </div> : viewMode === 'grid' ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-id="element-12983" data-mp="2aqhyvyb6tn2i">
                {filteredProducts.map(product => <div key={product.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow group" data-id="element-12984" data-mp="2eto02rjg0o24">
                    <div className="aspect-square relative overflow-hidden bg-gray-100" data-id="element-12985" data-mp="3d5qrrmjgsxcp">
                      <img src={product.imageUrl} alt={product.name} onError={e => {
                ;
                (e.currentTarget as HTMLImageElement).src = 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80';
              }} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 cursor-pointer" onClick={() => navigate(`/product-detail/${product.id}?shop=${shopId}`)} data-id="element-12986" data-mp="3r12r678kfbzy" />

                      {!product.isActive && <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center" data-id="element-12987" data-mp="127cj3069bwyi">
                          <span className="px-3 py-1 bg-gray-900 text-white text-xs font-medium rounded-full" data-id="element-12988" data-mp="t90h61zxxcqr">
                            {t('pages.Campaigns.inactive')}
                          </span>
                        </div>}
                      {product.discountedPrice && product.isActive && <div className="absolute top-3 right-3" data-id="element-12989" data-mp="oa5zj3dplu88">
                          <span className="px-2 py-1 bg-red-500 text-white text-xs font-bold rounded" data-id="element-12990" data-mp="9l04lrlwmq65">
                            {t('pages.ShopDetails.sale', 'Sale')}
                          </span>
                        </div>}
                    </div>
                    <div className="p-4" data-id="element-12991" data-mp="5iwqev7up7h6">
                      <div className="flex justify-between items-start mb-1" data-id="element-12992" data-mp="5hv3nthh2wjm">
                        <h3 className="font-medium text-gray-900 truncate pr-2" data-id="element-12993" data-mp="l1ftrbxgizg8">
                          {product.name}
                        </h3>
                        <div className="flex items-center gap-1" data-id="element-12994" data-mp="g2mdxcup6wcw">
                          <button onClick={() => handleEditProductClick(product)} className="p-1.5 text-gray-400 hover:text-[#9F140F] hover:bg-[#FEE7E5] rounded transition-colors" title={t('pages.ShopDetails.attrs.edit_product')} data-id="element-12995" data-mp="b1p17fsgiuis">
                            <EditIcon size={14} data-id="element-12996" data-mp="3temwvai7v175" />
                          </button>
                          <button onClick={() => toggleProductStatus(product.id)} className={`p-1.5 rounded transition-colors ${product.isActive ? 'text-gray-400 hover:text-red-600 hover:bg-red-50' : 'text-gray-400 hover:text-green-600 hover:bg-green-50'}`} title={product.isActive ? 'Deactivate product' : 'Activate product'} data-id="element-12997" data-mp="3ohwcwc3ajlgg">
                            {product.isActive ? <EyeIcon size={14} data-id="element-12998" data-mp="1yyof2gvzvji0" /> : <EyeIcon size={14} className="opacity-50" data-id="element-12999" data-mp="23ysvy80teg8r" />}
                          </button>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mb-3" data-id="element-13000" data-mp="1bb6jea9m019r">
                        {categories.find(c => c.id === product.categoryId)?.name || 'Uncategorized'}
                      </div>
                      <div className="flex items-center justify-between mt-auto" data-id="element-13001" data-mp="1l2krv7jjgbf1">
                        <div className="flex items-center gap-2" data-id="element-13002" data-mp="nj9k1incj1pj">
                          {product.discountedPrice ? <>
                              <span className="font-bold text-gray-900" data-id="element-13003" data-mp="2a9papllk03a">
                                {formatCurrencyAmount(product.discountedPrice, {
                        fractionDigits: 2
                      })}
                              </span>
                              <span className="text-xs text-gray-400 line-through" data-id="element-13004" data-mp="3udsu31onotot">
                                {formatCurrencyAmount(product.price, {
                        fractionDigits: 2
                      })}
                              </span>
                            </> : <span className="font-bold text-gray-900" data-id="element-13005" data-mp="36qaz6xa3hfhm">
                              {formatCurrencyAmount(product.price, {
                      fractionDigits: 2
                    })}
                            </span>}
                        </div>
                        <span className="text-xs text-gray-500" data-id="element-13006" data-mp="31rpvuozjqfoy">
                          {product.stockQuantity}{' '}
                          {t('pages.ShopDetails.in_stock', 'in stock')}
                        </span>
                      </div>
                    </div>
                  </div>)}
              </div> : <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-13007" data-mp="2n2ohiiuu289q">
                <table className="w-full text-left border-collapse" data-id="element-13008" data-mp="3127huphekfbh">
                  <thead data-id="element-13009" data-mp="310cvr7ueah5t">
                    <tr className="border-b border-gray-100 bg-gray-50/50" data-id="element-13010" data-mp="2w45xm188q9yf">
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13011" data-mp="3asit6x3f1qse">
                        {t('pages.ShopDetails.product')}
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13012" data-mp="35vs6vs10pyyj">
                        {t('pages.CampaignDetails.category')}
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13013" data-mp="3fpshg9f2jwen">
                        {t('pages.ShopDetails.price')}
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13014" data-mp="3rrsggzgtajre">
                        {t('pages.ShopDetails.inventory')}
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13015" data-mp="5gepmdkthyej">
                        {t('components.CampaignList.status')}
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-13016" data-mp="eh357cpv06tj">
                        {t('pages.CampaignDetails.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100" data-id="element-13017" data-mp="2l6818tkndwsq">
                    {filteredProducts.map(product => <tr key={product.id} className={`hover:bg-gray-50 transition-colors ${!product.isActive ? 'opacity-60' : ''}`} data-id="element-13018" data-mp="2q52ruhouwpne">
                        <td className="px-6 py-4" data-id="element-13019" data-mp="12h51vpzf51eo">
                          <div className="flex items-center gap-4" data-id="element-13020" data-mp="ygl88we20fxa">
                            <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0" data-id="element-13021" data-mp="yeqmjfuykjs5">
                              <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover" data-id="element-13022" data-mp="1q8aja9l0c1r3" />
                            </div>
                            <div data-id="element-13023" data-mp="1h9p5erqhdawn">
                              <div className="font-medium text-gray-900" data-id="element-13024" data-mp="1c8rmgcxnccu3">
                                {product.name}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5" data-id="element-13025" data-mp="5tqs2bo9s2pm">
                                {t('pages.ShopDetails.sku')} {product.sku}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-13026" data-mp="1thk7diutnmxn">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700" data-id="element-13027" data-mp="1fpcbdtbd4000">
                            {categories.find(c => c.id === product.categoryId)?.name || 'Uncategorized'}
                          </span>
                        </td>
                        <td className="px-6 py-4" data-id="element-13028" data-mp="2celbz2kgrl12">
                          {product.discountedPrice ? <div data-id="element-13029" data-mp="1yf2agsm5r7xq">
                              <div className="font-medium text-gray-900" data-id="element-13030" data-mp="1yi7vnwro1po6">
                                {formatCurrencyAmount(product.discountedPrice, {
                        fractionDigits: 2
                      })}
                              </div>
                              <div className="text-xs text-gray-400 line-through mt-0.5" data-id="element-13031" data-mp="s12yjgw4mml0">
                                {formatCurrencyAmount(product.price, {
                        fractionDigits: 2
                      })}
                              </div>
                            </div> : <div className="font-medium text-gray-900" data-id="element-13032" data-mp="ohc5e64hyasg">
                              {formatCurrencyAmount(product.price, {
                      fractionDigits: 2
                    })}
                            </div>}
                        </td>
                        <td className="px-6 py-4" data-id="element-13033" data-mp="aj4f2h1yu7q9">
                          <div className="text-sm text-gray-900" data-id="element-13034" data-mp="ajxhj5tw6b2l">
                            {product.stockQuantity}
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-13035" data-mp="3e5cyd62j2t96">
                          {product.isActive ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-green-100 text-green-800" data-id="element-13036" data-mp="397t97b4akw2g">
                              {t('pages.Campaigns.active')}
                            </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-600" data-id="element-13037" data-mp="1mw2n4jvte9th">
                              {t('pages.Campaigns.inactive')}
                            </span>}
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-13038" data-mp="z68dscapf3dz">
                          <div className="flex items-center justify-end gap-2" data-id="element-13039" data-mp="144i7eigs5vxh">
                            <button onClick={() => toggleProductStatus(product.id)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors" title={product.isActive ? 'Deactivate' : 'Activate'} data-id="element-13040" data-mp="146mj3mkx7z8b">
                              {product.isActive ? <EyeIcon size={16} data-id="element-13041" data-mp="3wli796ao029" /> : <EyeIcon size={16} className="opacity-50" data-id="element-13042" data-mp="3v9g4hb207fob" />}
                            </button>
                            <button onClick={() => handleEditProductClick(product)} className="p-1.5 text-gray-400 hover:text-[#9F140F] hover:bg-[#FEE7E5] rounded transition-colors" title={t('pages.ShopDetails.attrs.edit_product')} data-id="element-13043" data-mp="3lcjut20dcni6">
                              <EditIcon size={16} data-id="element-13044" data-mp="37e3np9ylguzi" />
                            </button>
                            <button onClick={() => handleDeleteProductClick(product.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title={t('pages.ShopDetails.attrs.delete_product')} data-id="element-13045" data-mp="1ajh2rjbjy2u5">
                              <TrashIcon size={16} data-id="element-13046" data-mp="9otjvhwelb3x" />
                            </button>
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </div>}

        {activeTab === 'categories' && <div className="space-y-6" data-id="element-13047" data-mp="3hymnqh3spjrm">
            <div className="flex justify-between items-center" data-id="element-13048" data-mp="3mvmteqwqgki7">
              <h2 className="text-lg font-medium text-gray-900" data-id="element-13049" data-mp="3mw4wa6dwhwz3">
                {t('pages.ShopDetails.product_categories')}
              </h2>
              <button className="flex items-center gap-2 px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-13050" data-mp="1l51lnh40giej">
                <PlusIcon size={16} data-id="element-13051" data-mp="35k7rnmu2c2df" />
                {t('pages.ShopDetails.add_category')}
              </button>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-13052" data-mp="3fclxors6bv5e">
              <table className="w-full text-left border-collapse" data-id="element-13053" data-mp="3al3wq3q5hukf">
                <thead data-id="element-13054" data-mp="3ajssy3wpgmtb">
                  <tr className="border-b border-gray-100 bg-gray-50/50" data-id="element-13055" data-mp="3ocs824sksf0c">
                    <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13056" data-mp="3oev6nd7snggo">
                      {t('pages.ShopDetails.category_name')}
                    </th>
                    <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13057" data-mp="23r8wns55idq">
                      {t('pages.CampaignDetails.description')}
                    </th>
                    <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13058" data-mp="23zb3g2m4nw8z">
                      {t('pages.ShopDetails.products')}
                    </th>
                    <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-13059" data-mp="3s1uqthao23fd">
                      {t('pages.CampaignDetails.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-13060" data-mp="p1l5l3387sk">
                  {categories.map(category => <tr key={category.id} className="hover:bg-gray-50 transition-colors" data-id="element-13061" data-mp="393ryotmtnqdc">
                      <td className="px-6 py-4" data-id="element-13062" data-mp="347l5m1fiy9ct">
                        <div className="font-medium text-gray-900" data-id="element-13063" data-mp="3j30yi34dbz7m">
                          {category.name}
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-13064" data-mp="ycibckp25fuo">
                        <div className="text-sm text-gray-500" data-id="element-13065" data-mp="yb77kkvm483k">
                          {category.description}
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-13066" data-mp="10wf0m0y2ulku">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700" data-id="element-13067" data-mp="10vn50q4hcnuk">
                          {category.productCount}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-13068" data-mp="vxbvkgi3kfcv">
                        <div className="flex items-center justify-end gap-2" data-id="element-13069" data-mp="vzy4okkmz2x7">
                          <button className="p-1.5 text-gray-400 hover:text-[#9F140F] hover:bg-[#FEE7E5] rounded transition-colors" title={t('pages.ShopDetails.attrs.edit_category')} data-id="element-13070" data-mp="1jnzdzvpn2ndo">
                            <EditIcon size={16} data-id="element-13071" data-mp="1evxw9u9pc4g9" />
                          </button>
                          <button className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title={t('pages.ShopDetails.attrs.delete_category')} data-id="element-13072" data-mp="22ls94bu0tt0i">
                            <TrashIcon size={16} data-id="element-13073" data-mp="2h6wkb5sjxjhh" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>}
      </div>
    </div>;
}